if (!customElements.get('atelier-demo-spotlight')) {
  customElements.define('atelier-demo-spotlight', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.abort = new AbortController();
      this.track = this.querySelector('.atelier-demo-spotlight-images');
      this.images = [...this.querySelectorAll('[data-demo-image]')];
      this.titles = [...this.querySelectorAll('[data-demo-target] span')];
      this.dots = [...this.querySelectorAll('[data-demo-dot]')];
      this.mobile = matchMedia('(max-width: 749px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.inView = false;
      const loadImage = (index) => {
        const template = this.images[index]?.querySelector('template[data-demo-lazy-image]');
        if (template) template.replaceWith(template.content.cloneNode(true));
      };
      const options = { signal: this.abort.signal };
      const sync = () => {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        if (!this.mobile.matches || !this.width) return;
        const position = Math.max(0, Math.min(this.images.length - 1, this.track.scrollLeft * this.direction / this.width));
        if (this.inView) {
          loadImage(Math.floor(position));
          loadImage(Math.ceil(position));
          loadImage(Math.ceil(position) + 1);
        }
        this.titles.forEach((title, index) => {
          const scale = Math.max(0, 1 - Math.abs(index - position));
          const origin = (index < position) === (this.direction === 1) ? 'right' : 'left';
          title.style.setProperty('--demo-line-scale', scale);
          title.style.setProperty('--demo-line-origin', origin);
          this.dots[index]?.style.setProperty('--demo-line-scale', Math.max(0, 2 * scale - 1));
          this.dots[index]?.style.setProperty('--demo-line-origin', origin);
        });
        const current = Math.round(position);
        if (current !== this.index) {
          this.dots[this.index]?.setAttribute('aria-current', 'false');
          this.dots[current]?.setAttribute('aria-current', 'true');
          this.index = current;
        }
      };
      const scrollTo = (index, behavior = 'smooth') => {
        loadImage(index);
        this.track.scrollTo({ left: index * this.width * this.direction, behavior: this.reducedMotion.matches ? 'instant' : behavior });
      };
      const mode = () => {
        loadImage(this.index);
        this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.index)));
        this.images.forEach((image, index) => { image.hidden = !this.mobile.matches && index !== this.index; });
        this.track.tabIndex = this.mobile.matches ? 0 : -1;
        this.direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        this.width = this.track.clientWidth;
        if (this.mobile.matches) scrollTo(this.index, 'instant');
        sync();
      };
      const show = (event) => {
        if (this.mobile.matches) return;
        const target = event.target.closest('[data-demo-target]');
        if (!target) return;
        this.index = Number(target.dataset.demoTarget);
        loadImage(this.index);
        this.images.forEach((image, index) => { image.hidden = index !== this.index; });
      };
      this.addEventListener('pointerover', show, options);
      this.addEventListener('focusin', show, options);
      this.addEventListener('click', (event) => {
        const target = event.target.closest('[data-demo-dot], button[data-demo-target]');
        if (!target) return;
        if (this.mobile.matches) scrollTo(Number(target.dataset.demoDot ?? target.dataset.demoTarget));
        else show(event);
      }, options);
      this.track.addEventListener('scroll', () => {
        if (this.mobile.matches && !this.frame) this.frame = requestAnimationFrame(sync);
      }, { ...options, passive: true });
      this.mobile.addEventListener('change', mode, options);
      this.resize = new ResizeObserver(() => {
        const width = this.track.clientWidth;
        if (width === this.width) return;
        this.width = width;
        if (this.mobile.matches) scrollTo(this.index, 'instant');
        sync();
      });
      this.resize.observe(this.track);
      this.lazyObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.inView = true;
        loadImage(this.index + 1);
        this.lazyObserver.disconnect();
      }, { rootMargin: '200px' });
      this.lazyObserver.observe(this.track);
      mode();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.lazyObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  });
}
